import React, { useState } from 'react';
import { Modal, Input, Button, message, Typography } from 'antd';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
    CREATE_SUPPORT_LOGIN_SHORT_CODE,
    CreateSupportLoginShortCodeResponse,
    CreateSupportLoginShortCodeVariables,
} from '../graphql/mutations/createSupportLoginShortCode';

const { Text } = Typography;

interface CreateShortCodeModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

const CreateShortCodeModal: React.FC<CreateShortCodeModalProps> = ({ open, onClose, onSuccess }) => {
    const { t } = useTranslation();
    const [targetAccountPhone, setTargetAccountPhone] = useState('');
    const [result, setResult] = useState<{ shortCode: string; phone: number | null } | null>(null);

    const [createShortCode, { loading }] = useMutation<
        CreateSupportLoginShortCodeResponse,
        CreateSupportLoginShortCodeVariables
    >(CREATE_SUPPORT_LOGIN_SHORT_CODE);

    const handleGenerate = async () => {
        const phone = parseInt(targetAccountPhone, 10);
        if (!targetAccountPhone || isNaN(phone)) {
            message.error(t('supportTools.supportLogin.modal.invalidPhone'));
            return;
        }

        try {
            const { data } = await createShortCode({
                variables: { targetAccountPhone: phone },
            });
            const resp = data?.createSupportLoginShortCode;
            if (resp?.success && resp.shortCode) {
                setResult({ shortCode: resp.shortCode, phone: resp.phone ?? null });
                message.success(t('supportTools.supportLogin.modal.generateSuccess'));
                onSuccess();
            } else {
                const messageKey = resp?.message;
                message.error(messageKey ? t(messageKey) : t('supportTools.supportLogin.modal.generateFailed'));
            }
        } catch (error: any) {
            message.error(t('supportTools.supportLogin.modal.generateFailed'));
        }
    };

    const handleClose = () => {
        setTargetAccountPhone('');
        setResult(null);
        onClose();
    };

    return (
        <Modal
            title={t('supportTools.supportLogin.modal.title')}
            open={open}
            onCancel={handleClose}
            footer={null}
            destroyOnClose
            maskClosable={false}
            keyboard={false}
        >
            <div className="flex flex-col gap-4 py-4">
                <div>
                    <Text className="mb-2 block">{t('supportTools.supportLogin.modal.targetAccountPhone')}</Text>
                    <Input
                        value={targetAccountPhone}
                        onChange={(e) => setTargetAccountPhone(e.target.value)}
                        placeholder={t('supportTools.supportLogin.modal.targetAccountPhonePlaceholder') ?? ''}
                        disabled={!!result}
                    />
                </div>

                {!result && (
                    <Button
                        type="primary"
                        onClick={handleGenerate}
                        loading={loading}
                        disabled={!targetAccountPhone}
                    >
                        <span className="font-bold">{t('supportTools.supportLogin.modal.generate')}</span>
                    </Button>
                )}

                {result && (
                    <div className="bg-gray-50 p-4 rounded">
                        <div className="mb-2 text-orange-500 text-sm">
                            {t('supportTools.supportLogin.modal.shortCodeExpireTip')}
                        </div>
                        <div className="mb-2">
                            <Text type="secondary">{t('supportTools.supportLogin.modal.shortCode')}</Text>
                            <div className="text-2xl font-bold tracking-widest mt-1">{result.shortCode}</div>
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default CreateShortCodeModal;
